import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Select, Tag, Card, Row, Col, Statistic, Popconfirm, message, Modal, Form, DatePicker, Input } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getLeaveRecords, getLeaveSummary, decideLeaveRecord, deleteLeaveRecord, createLeaveRecord } from '../api/leaves';
import { getUsers } from '../api/users';
import { apiErrorMessage } from '../utils/apiError';

const { RangePicker } = DatePicker;

const TYPE_LABELS = {
  dogum_izni: 'Doğum İzni',
  yillik_izin: 'Yıllık İzin',
  mazeret_izni: 'Mazeret İzni',
  yarim_gun: 'Yarım Gün',
};

const STATUS_TAGS = {
  pending: <Tag color="gold">Bekliyor</Tag>,
  approved: <Tag color="green">Onaylandı</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
};

const LeaveRequests = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const fetchRecords = useCallback(async (page = 1, status = statusFilter, type = typeFilter) => {
    setLoading(true);
    try {
      const res = await getLeaveRecords({ page, per_page: pagination.pageSize, status, type });
      setRecords(res.data.data);
      setPagination({ current: res.data.current_page, pageSize: res.data.per_page, total: res.data.total });
    } catch {
      message.error('İzin talepleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await getLeaveSummary();
      setSummary(res.data);
    } catch {
      message.error('Özet veriler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchUsers = async (value) => {
    setUserSearch(value);
    if (!value || value.length < 2) { setUserOptions([]); return; }
    setUserSearchLoading(true);
    try {
      const res = await getUsers({ search: value, per_page: 20 });
      setUserOptions(res.data.data || []);
    } catch {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleAddLeave = async (values) => {
    setAddLoading(true);
    try {
      await createLeaveRecord({
        user_id:  values.user_id,
        type:     values.type,
        start_at: values.range[0].format('YYYY-MM-DD HH:mm:ss'),
        end_at:   values.range[1].format('YYYY-MM-DD HH:mm:ss'),
        note:     values.note,
      });
      message.success('İzin kaydı oluşturuldu.');
      setAddModalOpen(false);
      addForm.resetFields();
      setUserOptions([]);
      fetchRecords(pagination.current);
      fetchSummary();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İzin kaydı oluşturulamadı.'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleDecide = async (id, decision) => {
    setDecidingId(id);
    try {
      await decideLeaveRecord(id, { decision });
      message.success(decision === 'approve' ? 'Onaylandı.' : 'Reddedildi.');
      fetchRecords(pagination.current);
      fetchSummary();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setDecidingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLeaveRecord(id);
      message.success('Kayıt silindi.');
      fetchRecords(pagination.current);
      fetchSummary();
    } catch {
      message.error('Kayıt silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Personel',
      render: (_, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users/${r.user?.id}`)}>
          {r.user ? `${r.user.name} ${r.user.surname}` : '-'}
        </Button>
      ),
    },
    { title: 'Tür', dataIndex: 'type', render: (t) => TYPE_LABELS[t] || t },
    {
      title: 'Tarih Aralığı',
      render: (_, r) => `${dayjs(r.start_at).format('DD.MM.YYYY HH:mm')} — ${dayjs(r.end_at).format('DD.MM.YYYY HH:mm')}`,
    },
    { title: 'Not', dataIndex: 'note', render: (n) => n || <span className="text-gray-400">-</span> },
    { title: 'Durum', dataIndex: 'status', render: (s) => STATUS_TAGS[s] },
    {
      title: 'Talep Eden',
      render: (_, r) => r.requester ? (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users/${r.requester.id}`)}>
          {r.requester.name} {r.requester.surname}
        </Button>
      ) : '-',
    },
    {
      title: 'Karar',
      render: (_, r) => r.decided_at ? (
        <span>
          {r.decider ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users/${r.decider.id}`)}>
              {r.decider.name} {r.decider.surname}
            </Button>
          ) : '-'}
          {' — '}{dayjs(r.decided_at).format('DD.MM.YYYY HH:mm')}
        </span>
      ) : <span className="text-gray-400">-</span>,
    },
    {
      title: 'İşlemler',
      render: (_, r) => (
        <div className="flex gap-2">
          {r.status === 'pending' && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />} loading={decidingId === r.id} onClick={() => handleDecide(r.id, 'approve')}>
                Onayla
              </Button>
              <Button size="small" danger icon={<CloseOutlined />} loading={decidingId === r.id} onClick={() => handleDecide(r.id, 'reject')}>
                Reddet
              </Button>
            </>
          )}
          <Popconfirm title="Kaydı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(r.id)} okText="Evet" cancelText="Hayır">
            <Button size="small" danger icon={<DeleteOutlined />}>Sil</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold mb-0">İzin Talepleri</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
          İzin Ekle
        </Button>
      </div>

      <Row gutter={16} className="mb-6">
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Bekleyen" value={summary?.by_status?.pending ?? 0} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#d48806' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Onaylanan" value={summary?.by_status?.approved ?? 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Reddedilen" value={summary?.by_status?.rejected ?? 0} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Toplam" value={pagination.total} />
          </Card>
        </Col>
      </Row>

      <Card>
        <div className="flex gap-3 mb-4">
          <Select
            placeholder="Durum filtrele"
            allowClear
            style={{ width: 160 }}
            onChange={(value) => { setStatusFilter(value); fetchRecords(1, value, typeFilter); }}
          >
            <Select.Option value="pending">Bekliyor</Select.Option>
            <Select.Option value="approved">Onaylandı</Select.Option>
            <Select.Option value="rejected">Reddedildi</Select.Option>
          </Select>
          <Select
            placeholder="Tür filtrele"
            allowClear
            style={{ width: 160 }}
            onChange={(value) => { setTypeFilter(value); fetchRecords(1, statusFilter, value); }}
          >
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <Select.Option key={value} value={value}>{label}</Select.Option>
            ))}
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo) => fetchRecords(paginationInfo.current, statusFilter, typeFilter)}
          pagination={{
            ...pagination,
            showTotal: (total) => `Toplam ${total} kayıt`,
          }}
        />
      </Card>

      <Modal
        title="İzin Ekle"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); addForm.resetFields(); setUserOptions([]); }}
        footer={null}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddLeave}>
          <Form.Item name="user_id" label="Personel" rules={[{ required: true, message: 'Personel seçiniz.' }]}>
            <Select
              showSearch
              placeholder="İsim ile ara..."
              filterOption={false}
              onSearch={searchUsers}
              loading={userSearchLoading}
              notFoundContent={userSearchLoading ? 'Aranıyor...' : userSearch.length < 2 ? 'En az 2 karakter girin' : 'Kullanıcı bulunamadı'}
            >
              {userOptions.map(u => (
                <Select.Option key={u.id} value={u.id}>{u.name} {u.surname} — {u.role}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="type" label="İzin Türü" rules={[{ required: true, message: 'Tür seçiniz.' }]}>
            <Select placeholder="Seçiniz">
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>{label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="range" label="Tarih Aralığı" rules={[{ required: true, message: 'Tarih aralığı seçiniz.' }]}>
            <RangePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="note" label="Not">
            <Input.TextArea rows={2} placeholder="İsteğe bağlı not..." />
          </Form.Item>

          <Form.Item className="mb-0 flex justify-end">
            <Button onClick={() => { setAddModalOpen(false); addForm.resetFields(); setUserOptions([]); }} className="mr-2">İptal</Button>
            <Button type="primary" htmlType="submit" loading={addLoading}>Kaydet</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LeaveRequests;
